import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  X,
  Loader2,
  AlertCircle,
  Pencil,
  Save,
  Server,
  ShieldCheck,
  Plus,
  Settings,
} from 'lucide-react';
import axiosInstance from '../api/axios';
import { resolve403ErrorMessage } from '../request/getApiErrorMessage.ts';
import ReportMultiSelect from './ReportMultiSelect';
import { useAuditYear } from '../contexts/AuditYearContext.tsx';

const CHIP_BASE = 'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium';
const CHIP_GREEN = `${CHIP_BASE} bg-green-100 text-green-700`;
const CHIP_GRAY = `${CHIP_BASE} bg-gray-100 text-gray-600`;
const CHIP_AMBER = `${CHIP_BASE} bg-amber-100 text-amber-800`;
const CHIP_BLUE = `${CHIP_BASE} bg-blue-100 text-blue-700`;

const INPUT_BASE =
  'w-full px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-700 disabled:cursor-not-allowed';

const SECTION_HEADER = 'text-xs font-bold tracking-wide uppercase text-slate-500 border-b border-gray-200 pb-2';
const FIELD_LABEL = 'block text-sm font-medium text-gray-700 mb-1';
const HINT = 'text-xs text-gray-500 mt-1.5';

function MetaItem({ label, children }) {
  return (
    <div className="min-w-0">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{label}</p>
      <div className="text-sm font-medium text-slate-700 truncate">{children}</div>
    </div>
  );
}

function formatTimestamp(value) {
  if (!value) return '-';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '-';
    return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return '-';
  }
}

/** ITGC-AC-01 → AC */
function domainCodeFromControlId(controlId) {
  if (!controlId) return '?';
  const parts = String(controlId).trim().split('-');
  if (parts.length >= 3 && parts[0].toUpperCase() === 'ITGC') return parts[1].toUpperCase();
  return '?';
}

// API values use hyphens (half-yearly, event-based); normalize to spaces for lookup.
const FREQUENCY_LABELS = {
  annual: 'Annual',
  'half yearly': 'Semi-Annual',
  quarterly: 'Quarterly',
  monthly: 'Monthly',
  'semi monthly': 'Semi-Monthly',
  weekly: 'Weekly',
  'bi weekly': 'Bi-Weekly',
  daily: 'Daily',
  'event based': 'Event-Based',
};

function formatLinkedControlFrequency(value) {
  if (!value) return '';
  const key = String(value).trim().toLowerCase().replace(/-/g, ' ');
  return FREQUENCY_LABELS[key] || value;
}

/** Map GET /api/systems/:id/linked-controls → same shape as /api/itgc-standard-controls. */
function mapLinkedControlToCatalogEntry(row) {
  const controlCode = row?.control_id || '';
  const riskRows =
    Array.isArray(row?.risks) && row.risks.length > 0
      ? row.risks
      : row?.risk
        ? [row.risk]
        : [];
  const riskIds = riskRows.map((r) => r?.id).filter(Boolean);
  const riskStatements = riskRows
    .map((r) => r?.risk_description)
    .filter((s) => typeof s === 'string' && s.trim());
  return {
    id: row?.id || controlCode,
    riskId: riskIds[0] || '',
    riskIds,
    // Domain now comes straight from the control's itgc_domain_id (joined by the
    // backend); fall back to parsing the control code for older rows.
    domainId: row?.domain_id || '',
    domainCode: row?.domain_code || domainCodeFromControlId(controlCode),
    domainName: row?.domain_name || '',
    controlCode,
    controlObjective: row?.objective || row?.title || '',
    controlWording: row?.control_wording_text || '',
    frequency: formatLinkedControlFrequency(row?.frequency),
    riskStatement: riskStatements[0] || '',
    riskStatements,
    isLinked: !!row?.is_linked,
  };
}

// Merge the manual-linked-controls rows with the org-copy rows pulled from
// /linked-controls, de-duping by control id (the two sources are normally
// disjoint, but guard against overlap). Manual rows win on conflict.
function mergeSelectedControlRows(manualRows, orgCopyRows) {
  const byId = new Map();
  for (const row of orgCopyRows || []) {
    if (row?.id) byId.set(row.id, row);
  }
  for (const row of manualRows || []) {
    if (row?.id) byId.set(row.id, row);
  }
  return Array.from(byId.values());
}

function linkedControlPatchLinks(selectedEntries) {
  return selectedEntries.flatMap((entry) => {
    const riskIds =
      Array.isArray(entry?.riskIds) && entry.riskIds.length > 0
        ? entry.riskIds.filter(Boolean)
        : entry?.riskId
          ? [entry.riskId]
          : [];
    if (riskIds.length === 0) {
      return [{ control_id: entry.id }];
    }
    return riskIds.map((riskId) => ({ control_id: entry.id, risk_id: riskId }));
  });
}

function emptyForm() {
  return {
    systemId: '',
    name: '',
    systemOwner: '',
    keySystem: false,
    socReport: false,
    soc1: false,
    soc2: false,
    rationale: '',
    // Selected report UUIDs — replaces the legacy free-text IPE textarea.
    // Written to system_reports junction on save via PUT body.reportIds.
    reportIds: [],
    isActive: true,
  };
}

// Per-period (year) coverage form. All fields default to "off / empty" so a
// freshly-picked year with no backend row renders as a clean slate the user
// can fill in.
function emptyCoverageForm() {
  return {
    keySystem: false,
    socReliance: false,
    soc1: false,
    soc1Type1: false,
    soc1Type1Periods: '',
    soc1Type2: false,
    soc1Type2Periods: '',
    soc2: false,
    soc2Type1: false,
    soc2Type1Periods: '',
    soc2Type2: false,
    soc2Type2Periods: '',
    bridgeLetter: false,
    bridgeLetterPeriod: '',
  };
}

function hydrateCoverageForm(row) {
  if (!row) return emptyCoverageForm();
  return {
    keySystem: !!row.keySystem,
    socReliance: !!row.socReliance,
    soc1: !!row.soc1,
    soc1Type1: !!row.soc1Type1,
    soc1Type1Periods: row.soc1Type1Periods || '',
    soc1Type2: !!row.soc1Type2,
    soc1Type2Periods: row.soc1Type2Periods || '',
    soc2: !!row.soc2,
    soc2Type1: !!row.soc2Type1,
    soc2Type1Periods: row.soc2Type1Periods || '',
    soc2Type2: !!row.soc2Type2,
    soc2Type2Periods: row.soc2Type2Periods || '',
    bridgeLetter: !!row.bridgeLetter,
    bridgeLetterPeriod: row.bridgeLetterPeriod || '',
  };
}

// Year picker options: current calendar year + three prior years.
// `kind` annotates each option for the dropdown label.
function buildYearOptions() {
  const current = new Date().getFullYear();
  return [
    { year: current, kind: 'current' },
    { year: current - 1, kind: 'past' },
    { year: current - 2, kind: 'past' },
    { year: current - 3, kind: 'past' },
  ];
}

function hydrateForm(system) {
  if (!system) return emptyForm();
  const cats = Array.isArray(system.categorization) ? system.categorization : [];
  const reports = Array.isArray(system.reports) ? system.reports : [];
  return {
    systemId: system.systemId || '',
    name: system.name || '',
    systemOwner: system.systemOwner || '',
    keySystem: !!system.keySystem,
    socReport: !!system.socReport,
    soc1: cats.includes('SOC1'),
    soc2: cats.includes('SOC2'),
    rationale: system.rationale || '',
    reportIds: reports.map((r) => r?.id).filter(Boolean),
    isActive: system.isActive !== false,
  };
}

// Tab keys for the two-tab system page. `details` is the legacy form;
// `itgc` is the new ITGC Domains multi-select that replaces the modal
// previously triggered from the Systems list's Link action.
const TAB_DETAILS = 'details';
const TAB_ITGC = 'itgc';
const TAB_MANUAL_ITGC = 'manual-itgc';

function SystemDetail() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const { selectedAuditYearId } = useAuditYear();
  const [searchParams, setSearchParams] = useSearchParams();

  const [system, setSystem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(searchParams.get('edit') === '1');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [form, setForm] = useState(emptyForm());

  // Active tab — initial value driven by ?tab= in the URL so the Systems
  // list's Link action can deep-link straight into the ITGC tab.
  const tabFromUrl = searchParams.get('tab');
  const initialTab =
    tabFromUrl === TAB_ITGC || tabFromUrl === TAB_MANUAL_ITGC ? tabFromUrl : TAB_DETAILS;
  const [activeTab, setActiveTab] = useState(initialTab);

  // ----- IT General Controls tab local state ----------------------------
  // Selected control UUIDs from GET /linked-controls — checkbox state in the picker.
  const [selectedControlIds, setSelectedControlIds] = useState(new Set());
  const [initialControlIds, setInitialControlIds] = useState(new Set());

  const [savingITGC, setSavingITGC] = useState(false);
  const [itgcError, setItgcError] = useState(null);
  const [itgcSavedAt, setItgcSavedAt] = useState(null);

  // Standard controls list from GET /api/systems/:id/linked-controls (mapped to
  // the same entry shape the picker already renders).
  const [standardCatalog, setStandardCatalog] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogError, setCatalogError] = useState(null);

  // ----- Manual ITGC Controls tab (org-created, no OICM) -----------------
  const [selectedManualControlIds, setSelectedManualControlIds] = useState(new Set());
  const [initialManualControlIds, setInitialManualControlIds] = useState(new Set());
  const [savingManualITGC, setSavingManualITGC] = useState(false);
  const [manualItgcError, setManualItgcError] = useState(null);
  const [manualItgcSavedAt, setManualItgcSavedAt] = useState(null);
  const [manualCatalog, setManualCatalog] = useState([]);
  const [manualCatalogLoading, setManualCatalogLoading] = useState(false);
  const [manualCatalogError, setManualCatalogError] = useState(null);

  const changeTab = (next) => {
    setActiveTab(next);
    const params = new URLSearchParams(searchParams);
    if (next === TAB_DETAILS) {
      params.delete('tab');
    } else {
      params.set('tab', next);
    }
    setSearchParams(params, { replace: true });
  };

  // Per-period coverage state. yearOptions is stable for the page's lifetime;
  // selectedYear/coverageForm change as the user navigates years.
  const yearOptions = useMemo(buildYearOptions, []);
  const [selectedYear, setSelectedYear] = useState(yearOptions[0].year);
  const [coverageForm, setCoverageForm] = useState(emptyCoverageForm());
  const [coverageLoading, setCoverageLoading] = useState(false);
  const [coverageError, setCoverageError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Use the list endpoint so usedIn is merged in (the single GET omits it).
        const response = await axiosInstance.get('/api/systems');
        if (cancelled) return;
        const all = response.data?.systems || [];
        const found = all.find(s => s.id === id);
        if (!found) {
          setError('System not found in your catalog.');
        } else {
          setSystem(found);
          setForm(hydrateForm(found));
        }
      } catch (err) {
        console.error('Error loading system:', err);
        if (!cancelled) setError(err?.response?.data?.error || 'Failed to load system');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  // Load the per-period coverage row for the currently-selected year. The
  // backend returns 404 when no row exists for (system, year), in which case
  // the form resets to its empty/default state so the user can fill it in
  // from scratch. Any other error surfaces as a warning above the form.
  useEffect(() => {
    if (!id) return undefined;
    let cancelled = false;
    (async () => {
      setCoverageLoading(true);
      setCoverageError(null);
      try {
        const res = await axiosInstance.get(`/api/systems/${id}/coverage/${selectedYear}`);
        if (cancelled) return;
        setCoverageForm(hydrateCoverageForm(res.data || null));
      } catch (err) {
        if (cancelled) return;
        if (err?.response?.status === 404) {
          // No row yet for this year — present an empty form.
          setCoverageForm(emptyCoverageForm());
        } else {
          console.error('Error loading coverage:', err);
          setCoverageError(err?.response?.data?.error || 'Failed to load coverage for this year');
          setCoverageForm(emptyCoverageForm());
        }
      } finally {
        if (!cancelled) setCoverageLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id, selectedYear]);

  const goBack = () => navigate('/sox-systems');
  const startEdit = () => {
    setIsEditing(true);
    setSaveError(null);
  };
  const cancelEdit = () => {
    setForm(hydrateForm(system));
    setIsEditing(false);
    setSaveError(null);
  };

  function applyLinkedControlsResponse(rows) {
    const mapped = rows.map(mapLinkedControlToCatalogEntry);
    setStandardCatalog(mapped);
    const linkedIds = new Set(
      mapped.filter((entry) => entry.isLinked).map((entry) => entry.id).filter(Boolean),
    );
    setSelectedControlIds(linkedIds);
    setInitialControlIds(new Set(linkedIds));
    return mapped;
  }

  function linkedControlsToSystemRefs(rows) {
    return (rows || [])
      .filter((row) => row?.is_linked)
      .map((row) => ({
        id: row.id,
        standardControlCode: row.control_id,
        name: row.objective || row.title || row.control_id,
        description: row.control_wording_text || '',
      }));
  }

  function applyManualLinkedControlsResponse(rows) {
    const mapped = rows.map(mapLinkedControlToCatalogEntry);
    setManualCatalog(mapped);
    const linked = new Set(
      mapped.filter((e) => e.isLinked && e.id).map((e) => e.id),
    );
    setSelectedManualControlIds(linked);
    setInitialManualControlIds(new Set(linked));
  }

  // Fetch the two sources that feed the "Selected ITGC Control" tab and merge
  // them: manual controls (GET /manual-linked-controls) + org copies derived
  // from the ITGC Control Library (the is_seeded=false rows of GET
  // /linked-controls). The manual PATCH response only returns pure manual
  // controls (its list query excludes OICM copies), so both after-load and
  // after-save go through this to keep the copies visible.
  // Links are per audit year: the linked/unlinked state reflects the selected year.
  const yearParams = selectedAuditYearId ? { audit_year_id: selectedAuditYearId } : undefined;

  async function fetchMergedSelectedControlRows() {
    const [manualRes, linkedRes] = await Promise.all([
      axiosInstance.get(`/api/systems/${id}/manual-linked-controls`, { params: yearParams }),
      axiosInstance.get(`/api/systems/${id}/linked-controls`, { params: yearParams }),
    ]);
    const manualRows = Array.isArray(manualRes.data?.controls) ? manualRes.data.controls : [];
    const linkedRows = Array.isArray(linkedRes.data?.controls) ? linkedRes.data.controls : [];
    const orgCopyRows = linkedRows.filter((row) => row?.is_seeded === false);
    return mergeSelectedControlRows(manualRows, orgCopyRows);
  }

  // Load ITGC controls for this system when the page opens.
  useEffect(() => {
    if (!id) return undefined;
    let cancelled = false;
    setCatalogLoading(true);
    setCatalogError(null);
    (async () => {
      try {
        const res = await axiosInstance.get(`/api/systems/${id}/itgc-control-library`, { params: yearParams });
        if (cancelled) return;
        const rows = Array.isArray(res.data?.controls) ? res.data.controls : [];
        applyLinkedControlsResponse(rows);
      } catch (err) {
        if (!cancelled) {
          setCatalogError(err?.response?.data?.error || 'Failed to load ITGC controls');
          setStandardCatalog([]);
        }
      } finally {
        if (!cancelled) setCatalogLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id, selectedAuditYearId]);

  // Load the "Selected ITGC Control" tab. This tab shows two sources merged:
  //  1. org-created manual ITGC controls (GET /manual-linked-controls), and
  //  2. the org copies derived from the ITGC Control Library — those come back
  //     from GET /linked-controls as the is_seeded=false rows (seeded masters
  //     stay in the Library tab, so we drop is_seeded=true rows here).
  useEffect(() => {
    if (!id) return undefined;
    let cancelled = false;
    setManualCatalogLoading(true);
    setManualCatalogError(null);
    (async () => {
      try {
        const mergedRows = await fetchMergedSelectedControlRows();
        if (cancelled) return;
        applyManualLinkedControlsResponse(mergedRows);
      } catch (err) {
        if (!cancelled) {
          setManualCatalogError(err?.response?.data?.error || 'Failed to load manual ITGC controls');
          setManualCatalog([]);
        }
      } finally {
        if (!cancelled) setManualCatalogLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id, selectedAuditYearId]);

  // Combined dirty flag — either tab surface is enough to enable Save.
  const itgcControlsDirty = useMemo(() => {
    if (selectedControlIds.size !== initialControlIds.size) return true;
    for (const controlId of selectedControlIds) if (!initialControlIds.has(controlId)) return true;
    return false;
  }, [selectedControlIds, initialControlIds]);

  const itgcDirty = itgcControlsDirty;

  const manualItgcControlsDirty = useMemo(() => {
    if (selectedManualControlIds.size !== initialManualControlIds.size) return true;
    for (const controlId of selectedManualControlIds) {
      if (!initialManualControlIds.has(controlId)) return true;
    }
    return false;
  }, [selectedManualControlIds, initialManualControlIds]);

  const toggleControlId = (controlId) => {
    if (!controlId) return;
    setSelectedControlIds((prev) => {
      const next = new Set(prev);
      if (next.has(controlId)) next.delete(controlId);
      else next.add(controlId);
      return next;
    });
  };

  const toggleManualControlId = (controlId) => {
    if (!controlId) return;
    setSelectedManualControlIds((prev) => {
      const next = new Set(prev);
      if (next.has(controlId)) next.delete(controlId);
      else next.add(controlId);
      return next;
    });
  };

  // Select-all / clear-all for the Selected ITGC Control table rows.
  const setAllManualControls = (select) => {
    setSelectedManualControlIds(
      select ? new Set(manualCatalog.map((e) => e.id).filter(Boolean)) : new Set(),
    );
  };

  // Save: persists the control links (PATCH). The system's domains are derived
  // from these controls, so the derived-domain chips update from the refreshed
  // linked-controls response — no separate domain write.
  const handleSaveITGC = async () => {
    if (!itgcDirty || savingITGC || !system) return;
    setSavingITGC(true);
    setItgcError(null);
    try {
      let updated = system;

      const selectedEntries = standardCatalog.filter(
        (entry) => entry?.id && selectedControlIds.has(entry.id),
      );
      const linkedControlsPayload = { links: linkedControlPatchLinks(selectedEntries) };
      if (selectedAuditYearId) linkedControlsPayload.audit_year_id = selectedAuditYearId;
      await axiosInstance.patch(`/api/systems/${id}/linked-controls`, linkedControlsPayload);
      // Re-read library + Selected ITGC Control sources so both tabs stay in sync.
      const [libraryRes, mergedRows] = await Promise.all([
        axiosInstance.get(`/api/systems/${id}/itgc-control-library`, { params: yearParams }),
        fetchMergedSelectedControlRows(),
      ]);
      const rows = Array.isArray(libraryRes.data?.controls) ? libraryRes.data.controls : [];
      applyLinkedControlsResponse(rows);
      applyManualLinkedControlsResponse(mergedRows);
      updated = {
        ...updated,
        itgcControls: linkedControlsToSystemRefs(rows),
      };

      setSystem(updated);
      setItgcSavedAt(new Date());
    } catch (err) {
      console.error('Save IT General Controls failed:', err);
      setItgcError(resolve403ErrorMessage(err, 'Something went wrong. Please try again.'));
    } finally {
      setSavingITGC(false);
    }
  };

  const handleSaveManualITGC = async () => {
    if (!manualItgcControlsDirty || savingManualITGC || !system) return;
    setSavingManualITGC(true);
    setManualItgcError(null);
    try {
      const selectedEntries = manualCatalog.filter(
        (entry) => entry?.id && selectedManualControlIds.has(entry.id),
      );
      const manualLinkedControlsPayload = { links: linkedControlPatchLinks(selectedEntries) };
      if (selectedAuditYearId) manualLinkedControlsPayload.audit_year_id = selectedAuditYearId;
      await axiosInstance.patch(`/api/systems/${id}/manual-linked-controls`, manualLinkedControlsPayload);
      // Re-fetch Selected ITGC Control sources + library so both tabs stay in sync.
      const [mergedRows, libraryRes] = await Promise.all([
        fetchMergedSelectedControlRows(),
        axiosInstance.get(`/api/systems/${id}/itgc-control-library`, { params: yearParams }),
      ]);
      applyManualLinkedControlsResponse(mergedRows);
      const libraryRows = Array.isArray(libraryRes.data?.controls) ? libraryRes.data.controls : [];
      applyLinkedControlsResponse(libraryRows);
      setSystem((prev) => ({
        ...prev,
        itgcControls: linkedControlsToSystemRefs(libraryRows),
      }));
      setManualItgcSavedAt(new Date());
    } catch (err) {
      console.error('Save Manual ITGC Controls failed:', err);
      setManualItgcError(resolve403ErrorMessage(err, 'Something went wrong. Please try again.'));
    } finally {
      setSavingManualITGC(false);
    }
  };

  const cancelITGC = () => {
    setSelectedControlIds(new Set(initialControlIds));
    setItgcError(null);
  };

  const cancelManualITGC = () => {
    setSelectedManualControlIds(new Set(initialManualControlIds));
    setManualItgcError(null);
  };

  const selectedControlEditReturnPath = `${location.pathname}${location.search || ''}`;
  const handleEditSelectedControl = (controlCode) => {
    const code = String(controlCode || '').trim();
    if (!code) return;
    navigate(`/sox-controls/view/${encodeURIComponent(code)}`, {
      state: { returnPath: selectedControlEditReturnPath },
    });
  };

  const handleSave = async () => {
    if (!system?.id || saving) return;
    if (!form.name.trim()) {
      setSaveError('Display name is required.');
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const categorization = [];
      if (form.socReport && form.soc1) categorization.push('SOC1');
      if (form.socReport && form.soc2) categorization.push('SOC2');

      const systemPayload = {
        systemId: form.systemId.trim(),
        name: form.name.trim(),
        systemOwner: form.systemOwner.trim(),
        keySystem: form.keySystem,
        socReport: form.socReport,
        categorization,
        rationale: form.rationale.trim(),
        // Replace the legacy free-text IPE list with the structured reportIds
        // junction-write. Sending an explicit [] when the user clears the
        // selection still triggers ReplaceLinksForSystem to soft-delete
        // every existing link, matching what the UI shows.
        reportIds: Array.isArray(form.reportIds) ? form.reportIds : [],
        ipe: [],
        isActive: form.isActive,
      };
      const coveragePayload = {
        keySystem: coverageForm.keySystem,
        socReliance: coverageForm.socReliance,
        // Sub-fields only count when their parent toggle is on — emit clean
        // false/empty values otherwise so a turn-off becomes authoritative.
        soc1: coverageForm.socReliance && coverageForm.soc1,
        soc1Type1: coverageForm.socReliance && coverageForm.soc1 && coverageForm.soc1Type1,
        soc1Type1Periods:
          coverageForm.socReliance && coverageForm.soc1 && coverageForm.soc1Type1
            ? coverageForm.soc1Type1Periods.trim()
            : '',
        soc1Type2: coverageForm.socReliance && coverageForm.soc1 && coverageForm.soc1Type2,
        soc1Type2Periods:
          coverageForm.socReliance && coverageForm.soc1 && coverageForm.soc1Type2
            ? coverageForm.soc1Type2Periods.trim()
            : '',
        soc2: coverageForm.socReliance && coverageForm.soc2,
        soc2Type1: coverageForm.socReliance && coverageForm.soc2 && coverageForm.soc2Type1,
        soc2Type1Periods:
          coverageForm.socReliance && coverageForm.soc2 && coverageForm.soc2Type1
            ? coverageForm.soc2Type1Periods.trim()
            : '',
        soc2Type2: coverageForm.socReliance && coverageForm.soc2 && coverageForm.soc2Type2,
        soc2Type2Periods:
          coverageForm.socReliance && coverageForm.soc2 && coverageForm.soc2Type2
            ? coverageForm.soc2Type2Periods.trim()
            : '',
        bridgeLetter: coverageForm.bridgeLetter,
        bridgeLetterPeriod: coverageForm.bridgeLetter ? coverageForm.bridgeLetterPeriod.trim() : '',
      };

      // Write both rows in parallel. Both must succeed before exiting edit
      // mode; otherwise show whatever error came back first.
      const [systemRes, coverageRes] = await Promise.all([
        axiosInstance.put(`/api/systems/${system.id}`, systemPayload),
        axiosInstance.put(`/api/systems/${system.id}/coverage/${selectedYear}`, coveragePayload),
      ]);

      const updated = systemRes.data || {};
      // Preserve usedIn from the list-derived record since the single response doesn't include it.
      const merged = { ...system, ...updated, usedIn: system.usedIn };
      setSystem(merged);
      setForm(hydrateForm(merged));
      setCoverageForm(hydrateCoverageForm(coverageRes.data || null));
      setIsEditing(false);
    } catch (err) {
      console.error('Save system failed:', err);
      setSaveError(resolve403ErrorMessage(err, err?.response?.data?.error || 'Failed to save system'));
    } finally {
      setSaving(false);
    }
  };

  const createdLabel = useMemo(() => formatTimestamp(system?.createdAt), [system?.createdAt]);
  const updatedLabel = useMemo(() => formatTimestamp(system?.updatedAt), [system?.updatedAt]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-2">
          <Loader2 className="h-6 w-6 text-blue-600 animate-spin" />
          <p className="text-sm text-gray-500">Loading system…</p>
        </div>
      </div>
    );
  }

  if (error || !system) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 max-w-md w-full text-center">
          <Server className="h-10 w-10 text-gray-300 mx-auto mb-3" />
          <p className="text-sm text-gray-700 mb-4">{error || 'System not found.'}</p>
          <button
            type="button"
            onClick={goBack}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to systems
          </button>
        </div>
      </div>
    );
  }

  const usedIn = Array.isArray(system.usedIn) ? system.usedIn : [];
  const linkedReports = Array.isArray(system.reports) ? system.reports : [];

  return (
    <div className="min-h-screen bg-gray-50 overflow-x-hidden">
      {/* Sticky top header */}
      <div className="bg-white border-b border-gray-200 px-4 sm:px-6 lg:px-8 py-4 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto flex items-center gap-3 min-w-0">
          <button
            type="button"
            onClick={goBack}
            className="shrink-0 p-2 -ml-2 hover:bg-gray-100 rounded-full transition-colors"
            aria-label="Back to systems"
          >
            <ArrowLeft className="h-5 w-5 text-gray-500" />
          </button>
          <div className="min-w-0 flex-1">
            <div className="flex items-baseline gap-2 min-w-0">
              <h1 className="text-xl font-semibold text-gray-900 truncate">
                {isEditing ? 'Edit system' : 'System'}
              </h1>
              <span className="text-sm font-medium text-gray-500 truncate">{system.name}</span>
            </div>
            <p className="text-sm text-gray-500 truncate">
              {isEditing
                ? 'Update the catalog record. System code stays stable as a stable identifier.'
                : 'Catalog record details and downstream usage.'}
            </p>
          </div>
          {!isEditing && activeTab === TAB_DETAILS && (
            <button
              type="button"
              onClick={startEdit}
              className="shrink-0 inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              <Pencil className="h-4 w-4" />
              Edit
            </button>
          )}
          <button
            type="button"
            onClick={goBack}
            className="shrink-0 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
            aria-label="Close"
            title="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 min-w-0">
        {/* Meta strip */}
        <div className="bg-slate-50 border border-gray-200 rounded-lg px-4 py-3 mb-4 flex flex-wrap items-start gap-x-8 gap-y-3">
          <MetaItem label="System code">{system.systemId || '-'}</MetaItem>
          <MetaItem label="Status">
            <span className={system.isActive === false ? CHIP_GRAY : CHIP_GREEN}>
              {system.isActive === false ? 'Inactive' : 'Active'}
            </span>
          </MetaItem>
          <MetaItem label="Created">{createdLabel}</MetaItem>
          <MetaItem label="Last Updated">{updatedLabel}</MetaItem>
        </div>

        {/* Tabs — Details = legacy catalog/coverage form; ITGC Domains =
            multi-select that replaced the Systems list "Link" modal */}
        <div className="border-b border-gray-200 mb-4 flex items-center gap-6">
          {[
            { key: TAB_DETAILS, label: 'Details' },
            { key: TAB_ITGC, label: 'ITGC Control Library' },
            { key: TAB_MANUAL_ITGC, label: 'Selected ITGC Control' },
          ].map((t) => {
            const active = activeTab === t.key;
            const badgeCount =
              t.key === TAB_ITGC
                ? initialControlIds.size
                : t.key === TAB_MANUAL_ITGC
                  ? initialManualControlIds.size
                  : 0;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => changeTab(t.key)}
                className={`relative -mb-px py-2 text-sm font-medium border-b-2 transition-colors ${
                  active
                    ? 'border-blue-600 text-blue-700'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <span className="inline-flex items-center gap-1.5">
                  {(t.key === TAB_ITGC || t.key === TAB_MANUAL_ITGC) && (
                    <ShieldCheck className="h-4 w-4" />
                  )}
                  {t.label}
                  {badgeCount > 0 ? (
                    <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                      active ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'
                    }`}>
                      {badgeCount}
                    </span>
                  ) : null}
                </span>
              </button>
            );
          })}
        </div>

        {activeTab === TAB_DETAILS && saveError && (
          <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 mb-4 flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-red-600 shrink-0" />
            <p className="text-sm text-red-800">{saveError}</p>
          </div>
        )}

        {activeTab === TAB_ITGC && (
          <ITGCTab
            dirty={itgcDirty}
            saving={savingITGC}
            error={itgcError}
            savedAt={itgcSavedAt}
            onSave={handleSaveITGC}
            onCancel={cancelITGC}
            currentControls={Array.isArray(system.itgcControls) ? system.itgcControls : []}
            standardCatalog={standardCatalog}
            catalogLoading={catalogLoading}
            catalogError={catalogError}
            selectedControlIds={selectedControlIds}
            onToggleControlId={toggleControlId}
            variant="standard"
          />
        )}

        {activeTab === TAB_MANUAL_ITGC && (
          <ITGCTab
            dirty={manualItgcControlsDirty}
            saving={savingManualITGC}
            error={manualItgcError}
            savedAt={manualItgcSavedAt}
            onSave={handleSaveManualITGC}
            onCancel={cancelManualITGC}
            currentControls={[]}
            standardCatalog={manualCatalog}
            catalogLoading={manualCatalogLoading}
            catalogError={manualCatalogError}
            selectedControlIds={selectedManualControlIds}
            onToggleControlId={toggleManualControlId}
            onSetAllControls={setAllManualControls}
            onEditControl={handleEditSelectedControl}
            variant="manual"
          />
        )}

        {activeTab === TAB_DETAILS && (
        <div className="space-y-4">
          {/* Summary card */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 md:p-5 space-y-4">
            <h2 className={SECTION_HEADER}>Summary</h2>

            <div>
              <label className={FIELD_LABEL}>System code</label>
              {isEditing ? (
                <input
                  type="text"
                  value={form.systemId}
                  onChange={(e) => setForm({ ...form, systemId: e.target.value })}
                  className={INPUT_BASE}
                />
              ) : (
                <p className="text-sm text-gray-900">{system.systemId || '—'}</p>
              )}
              <p className={HINT}>Stable code used in matrices and working papers.</p>
            </div>

            <div>
              <label className={FIELD_LABEL}>
                Display name {isEditing && <span className="text-red-600">*</span>}
              </label>
              {isEditing ? (
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className={INPUT_BASE}
                />
              ) : (
                <p className="text-sm text-gray-900">{system.name}</p>
              )}
            </div>

            <div>
              <label className={FIELD_LABEL}>System owner</label>
              {isEditing ? (
                <input
                  type="text"
                  value={form.systemOwner}
                  onChange={(e) => setForm({ ...form, systemOwner: e.target.value })}
                  className={INPUT_BASE}
                  placeholder="e.g. Jamie Chen · Finance Ops"
                />
              ) : (
                <p className="text-sm text-gray-900">{system.systemOwner || '—'}</p>
              )}
            </div>

            <div>
              <label className={FIELD_LABEL}>Reason for in scope</label>
              {isEditing ? (
                <textarea
                  value={form.rationale}
                  onChange={(e) => setForm({ ...form, rationale: e.target.value })}
                  className={`${INPUT_BASE} min-h-[88px] resize-y`}
                  placeholder="Plain-language reason this system is in the inventory for the period…"
                />
              ) : (
                <p className="text-sm text-gray-900 whitespace-pre-line">{system.rationale || '—'}</p>
              )}
            </div>
          </div>

          {/* Coverage & evidence */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 md:p-5 space-y-5">
            <h2 className={SECTION_HEADER}>Coverage &amp; evidence</h2>

            {/* Period picker — drives every per-year field below. */}
            <div>
              <label className={FIELD_LABEL}>Period</label>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(Number(e.target.value))}
                className={`${INPUT_BASE} max-w-xs`}
                disabled={coverageLoading}
              >
                {yearOptions.map((opt) => (
                  <option key={opt.year} value={opt.year}>
                    {opt.year} ({opt.kind})
                  </option>
                ))}
              </select>
              {coverageLoading && (
                <p className={`${HINT} flex items-center gap-1.5`}>
                  <Loader2 className="h-3 w-3 animate-spin" /> Loading {selectedYear} coverage…
                </p>
              )}
              {coverageError && !coverageLoading && (
                <p className="text-xs text-red-600 mt-1.5 flex items-center gap-1.5">
                  <AlertCircle className="h-3 w-3" /> {coverageError}
                </p>
              )}
            </div>

            {/* Scope (per period) */}
            <div>
              <label className={FIELD_LABEL}>Scope</label>
              {isEditing ? (
                <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                  <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                    <input
                      type="radio"
                      name="coverage-scope"
                      checked={coverageForm.keySystem}
                      onChange={() => setCoverageForm({ ...coverageForm, keySystem: true })}
                      className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                    />
                    Key
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                    <input
                      type="radio"
                      name="coverage-scope"
                      checked={!coverageForm.keySystem}
                      onChange={() => setCoverageForm({ ...coverageForm, keySystem: false })}
                      className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                    />
                    Non-Key
                  </label>
                </div>
              ) : (
                <span className={coverageForm.keySystem ? CHIP_GREEN : CHIP_GRAY}>
                  {coverageForm.keySystem ? 'Key' : 'Non-Key'}
                </span>
              )}
            </div>

            {/* SOC reliance (per period) */}
            <div>
              <label className={FIELD_LABEL}>SOC reliance</label>
              {isEditing ? (
                <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                  <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                    <input
                      type="radio"
                      name="coverage-soc-reliance"
                      checked={coverageForm.socReliance}
                      onChange={() => setCoverageForm({ ...coverageForm, socReliance: true })}
                      className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                    />
                    Yes
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                    <input
                      type="radio"
                      name="coverage-soc-reliance"
                      checked={!coverageForm.socReliance}
                      onChange={() => setCoverageForm({ ...coverageForm, socReliance: false })}
                      className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                    />
                    No
                  </label>
                </div>
              ) : (
                <span className={coverageForm.socReliance ? CHIP_GREEN : CHIP_GRAY}>
                  {coverageForm.socReliance ? 'Yes' : 'No'}
                </span>
              )}
            </div>

            {/* SOC1 / SOC2 sub-tree, shown only when SOC reliance = Yes */}
            {coverageForm.socReliance && (
              <div className="rounded-md border border-gray-200 bg-gray-50 p-3 space-y-3">
                {isEditing ? (
                  <SocFlatEditor coverageForm={coverageForm} setCoverageForm={setCoverageForm} inputClass={INPUT_BASE} />
                ) : (
                  <>
                    {/* SOC 1 read view */}
                    <SocFamilyBlock
                      isEditing={false}
                      chipClass={CHIP_AMBER}
                      accentRingClass="focus:ring-amber-500"
                      accentTextClass="text-amber-600"
                      label="SOC 1"
                      enabled={coverageForm.soc1}
                      type1Enabled={coverageForm.soc1Type1}
                      type1Periods={coverageForm.soc1Type1Periods}
                      type2Enabled={coverageForm.soc1Type2}
                      type2Periods={coverageForm.soc1Type2Periods}
                    />

                    {/* SOC 2 read view */}
                    <SocFamilyBlock
                      isEditing={false}
                      chipClass={CHIP_BLUE}
                      accentRingClass="focus:ring-blue-500"
                      accentTextClass="text-blue-600"
                      label="SOC 2"
                      enabled={coverageForm.soc2}
                      type1Enabled={coverageForm.soc2Type1}
                      type1Periods={coverageForm.soc2Type1Periods}
                      type2Enabled={coverageForm.soc2Type2}
                      type2Periods={coverageForm.soc2Type2Periods}
                    />
                  </>
                )}
              </div>
            )}

            {/* Bridge letter (per period) */}
            <div>
              <label className={FIELD_LABEL}>Bridge letter</label>
              {isEditing ? (
                <>
                  <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mb-2">
                    <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                      <input
                        type="radio"
                        name="coverage-bridge-letter"
                        checked={coverageForm.bridgeLetter}
                        onChange={() => setCoverageForm({ ...coverageForm, bridgeLetter: true })}
                        className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                      />
                      Yes
                    </label>
                    <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                      <input
                        type="radio"
                        name="coverage-bridge-letter"
                        checked={!coverageForm.bridgeLetter}
                        onChange={() => setCoverageForm({ ...coverageForm, bridgeLetter: false })}
                        className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                      />
                      No
                    </label>
                  </div>
                  {coverageForm.bridgeLetter && (
                    <input
                      type="text"
                      value={coverageForm.bridgeLetterPeriod}
                      onChange={(e) => setCoverageForm({ ...coverageForm, bridgeLetterPeriod: e.target.value })}
                      placeholder="Period (e.g. Aug–Dec 2025)"
                      className={INPUT_BASE}
                    />
                  )}
                </>
              ) : (
                <div className="flex items-center gap-2">
                  <span className={coverageForm.bridgeLetter ? CHIP_GREEN : CHIP_GRAY}>
                    {coverageForm.bridgeLetter ? 'Yes' : 'No'}
                  </span>
                  {coverageForm.bridgeLetter && coverageForm.bridgeLetterPeriod && (
                    <span className="text-sm text-gray-800">· {coverageForm.bridgeLetterPeriod}</span>
                  )}
                </div>
              )}
            </div>

            {/* Active in catalog (system-level, kept here for layout convenience). */}
            <div>
              <label className={FIELD_LABEL}>Active in catalog</label>
              {isEditing ? (
                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.isActive}
                    onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                    className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  Active
                </label>
              ) : (
                <span className={form.isActive === false ? CHIP_GRAY : CHIP_GREEN}>
                  {form.isActive === false ? 'Inactive' : 'Active'}
                </span>
              )}
            </div>

            <div>
              <label className={FIELD_LABEL}>Reports &amp; artifacts (IPE)</label>
              {isEditing ? (
                <>
                  <ReportMultiSelect
                    value={form.reportIds}
                    onChange={(next) => setForm({ ...form, reportIds: next })}
                    systemId={id}
                    returnPath={`${location.pathname}${location.search || ''}`}
                  />
                  <p className={HINT}>
                    Pick reports from the catalog — or use “Add new report” to create one
                    and have it auto-linked back to this system.
                  </p>
                </>
              ) : linkedReports.length === 0 ? (
                <p className="text-sm text-gray-500">—</p>
              ) : (
                <ul className="flex flex-wrap gap-1.5">
                  {linkedReports.map((r) => (
                    <li
                      key={r.id}
                      className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-indigo-50 text-indigo-700 border border-indigo-200 rounded"
                    >
                      {r.name}
                      {r.reportId ? <span className="text-indigo-400 font-mono">{r.reportId}</span> : null}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          {/* Usage in processes */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 md:p-5 space-y-3">
            <h2 className={SECTION_HEADER}>
              Usage <span className="text-slate-400 font-medium normal-case">(read-only — driven by active flowcharts)</span>
            </h2>
            {usedIn.length === 0 ? (
              <p className="text-sm text-gray-500">This system isn&rsquo;t referenced on any active process flowchart yet.</p>
            ) : (
              <ul className="divide-y divide-gray-100 border border-gray-100 rounded-md">
                {usedIn.map((u, i) => (
                  <li key={`${u.processId || ''}-${i}`} className="px-3 py-2 flex items-center justify-between">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{u.processName || '—'}</p>
                      <p className="text-xs text-gray-500 truncate">{u.cycleName || ''}</p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Edit-mode footer actions */}
          {isEditing && (
            <div className="flex flex-wrap justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={cancelEdit}
                disabled={saving}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-blue-600 rounded-lg hover:bg-blue-700 disabled:bg-blue-300 disabled:cursor-not-allowed"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                {saving ? 'Saving…' : 'Save changes'}
              </button>
            </div>
          )}
        </div>
        )}
      </div>
    </div>
  );
}

// Column set for the "Selected ITGC Control" table. Domain Code + Domain Name
// come first, then the control detail. All are toggleable via the gear menu.
const MANUAL_ITGC_COLUMNS = [
  { key: 'domainCode', label: 'Domain Code' },
  { key: 'domainName', label: 'Domain Name' },
  { key: 'controlCode', label: 'Control ID' },
  { key: 'objective', label: 'Objective' },
  { key: 'description', label: 'Description' },
  { key: 'risk', label: 'Risk' },
  { key: 'frequency', label: 'Frequency' },
];

const MANUAL_ITGC_DEFAULT_VISIBILITY = MANUAL_ITGC_COLUMNS.reduce(
  (acc, col) => ({ ...acc, [col.key]: true }),
  {},
);

function renderManualITGCCell(key, entry, risk) {
  switch (key) {
    case 'domainCode':
      return (
        <span className="font-mono text-xs text-gray-600 whitespace-nowrap">
          {entry.domainCode || '—'}
        </span>
      );
    case 'domainName':
      return <span className="whitespace-nowrap text-gray-700">{entry.domainName || '—'}</span>;
    case 'controlCode':
      return (
        <span className="font-mono text-xs text-gray-600 whitespace-nowrap">
          {entry.controlCode || '—'}
        </span>
      );
    case 'objective':
      return (
        <div className="max-w-[16rem] truncate text-gray-900" title={entry.controlObjective || ''}>
          {entry.controlObjective || '—'}
        </div>
      );
    case 'description':
      return (
        <div className="max-w-[20rem] truncate text-gray-600" title={entry.controlWording || ''}>
          {entry.controlWording || '—'}
        </div>
      );
    case 'risk':
      return risk ? (
        <div className="max-w-[16rem] truncate text-red-700" title={risk}>
          {risk}
        </div>
      ) : (
        <span className="text-gray-400">—</span>
      );
    case 'frequency':
      return <span className="whitespace-nowrap text-gray-600">{entry.frequency || '—'}</span>;
    default:
      return null;
  }
}

// Column-visibility gear for the "Selected ITGC Control" table. Rendered in the
// header beside "Add control". Column toggles + reset only (row select-all lives
// in the table header, not here).
function ManualITGCColumnGear({ columnVisibility, setColumnVisibility }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onPointerDown = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [menuOpen]);

  const toggleColumn = (key) =>
    setColumnVisibility((prev) => ({ ...prev, [key]: !prev[key] }));
  const resetToDefault = () => setColumnVisibility(MANUAL_ITGC_DEFAULT_VISIBILITY);

  return (
    <div className="relative" ref={menuRef}>
      <button
        type="button"
        onClick={() => setMenuOpen((prev) => !prev)}
        aria-label="Show or hide columns"
        aria-expanded={menuOpen}
        className="inline-flex items-center justify-center h-9 w-9 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
      >
        <Settings className="h-4 w-4" aria-hidden />
      </button>
      {menuOpen && (
        <div className="absolute right-0 mt-2 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
          <div className="px-3 py-2 border-b border-gray-100">
            <p className="text-sm font-semibold text-gray-900">Show columns</p>
            <p className="text-xs text-gray-500">Select columns to display in this table.</p>
          </div>
          <div className="p-3 space-y-2 max-h-72 overflow-auto">
            {MANUAL_ITGC_COLUMNS.map((col) => (
              <label
                key={col.key}
                className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer py-0.5"
              >
                <input
                  type="checkbox"
                  checked={!!columnVisibility[col.key]}
                  onChange={() => toggleColumn(col.key)}
                  className="h-4 w-4 cursor-pointer rounded border border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                {col.label}
              </label>
            ))}
          </div>
          <div className="px-3 py-2 border-t border-gray-100 flex justify-end">
            <button
              type="button"
              onClick={resetToDefault}
              className="text-xs font-medium text-blue-700 hover:text-blue-900"
            >
              Reset to default
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ManualITGCTable renders the "Selected ITGC Control" tab as a table. Column
// visibility comes from the header gear; the header checkbox selects/clears all
// rows. Data + per-row checkbox behavior are identical to the card layout.
function ManualITGCTable({
  standardCatalog,
  selectedControlIds,
  onToggleControlId,
  onSetAllControls,
  onEditControl,
  columnVisibility,
  saving,
}) {
  const visibleColumns = MANUAL_ITGC_COLUMNS.filter((col) => columnVisibility[col.key]);
  const selectableIds = standardCatalog.map((e) => e.id).filter(Boolean);
  const allRowsSelected =
    selectableIds.length > 0 && selectableIds.every((cid) => selectedControlIds.has(cid));

  // Expand to one row per (control, risk). A control with N risks yields N rows
  // (control detail repeated); a control with no risk yields a single row. The
  // checkbox on every row is tied to the CONTROL, so all rows for a control
  // toggle together and the save payload dedupes to unique control ids.
  const rows = standardCatalog.flatMap((entry) => {
    const riskStatements =
      Array.isArray(entry.riskStatements) && entry.riskStatements.length > 0
        ? entry.riskStatements
        : entry.riskStatement
          ? [entry.riskStatement]
          : [];
    const baseKey = entry.id || entry.controlCode;
    if (riskStatements.length === 0) {
      return [{ entry, risk: null, rowKey: `${baseKey}` }];
    }
    return riskStatements.map((risk, idx) => ({ entry, risk, rowKey: `${baseKey}-r-${idx}` }));
  });

  return (
    <div className="overflow-x-auto border border-gray-200 rounded-lg">
      <table className="min-w-full text-sm">
        <thead className="bg-gray-50">
          <tr className="text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
            <th className="px-3 py-2 w-10">
              <input
                type="checkbox"
                aria-label="Select all controls"
                className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 disabled:cursor-not-allowed"
                checked={allRowsSelected}
                onChange={(e) => onSetAllControls?.(e.target.checked)}
                disabled={saving || selectableIds.length === 0}
              />
            </th>
            {visibleColumns.map((col) => (
              <th key={col.key} className="px-3 py-2 whitespace-nowrap">
                {col.label}
              </th>
            ))}
            <th className="px-3 py-2 whitespace-nowrap w-16">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map(({ entry, risk, rowKey }) => {
            const controlId = entry.id;
            const checked = selectedControlIds.has(controlId);
            const controlCode = entry.controlCode;
            return (
              <tr key={rowKey} className={checked ? 'bg-blue-50/40' : 'hover:bg-gray-50'}>
                <td className="px-3 py-2 align-top">
                  <input
                    type="checkbox"
                    className="mt-0.5 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 disabled:cursor-not-allowed"
                    checked={checked}
                    onChange={() => onToggleControlId(controlId)}
                    disabled={saving}
                  />
                </td>
                {visibleColumns.map((col) => (
                  <td key={col.key} className="px-3 py-2 align-top">
                    {renderManualITGCCell(col.key, entry, risk)}
                  </td>
                ))}
                <td className="px-3 py-2 align-top">
                  <button
                    type="button"
                    onClick={() => onEditControl?.(controlCode)}
                    disabled={saving || !controlCode}
                    className="inline-flex items-center justify-center h-8 w-8 text-gray-500 hover:text-blue-700 hover:bg-blue-50 rounded-md disabled:opacity-40 disabled:cursor-not-allowed"
                    aria-label={controlCode ? `Edit control ${controlCode}` : 'Edit control'}
                    title="Edit control"
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ITGCTab renders the IT General Controls tab: a read-only strip of the
// domains the system is in scope for (DERIVED from the linked controls), then a
// checkbox list of standard controls grouped by domain, then the system's
// currently-saved controls as a read-only chip strip, then the Save / Reset
// footer. Lives in this file because it only consumes SystemDetail's local state.
function ITGCTab({
  dirty,
  saving,
  error,
  savedAt,
  onSave,
  onCancel,
  currentControls,
  standardCatalog,
  catalogLoading,
  catalogError,
  selectedControlIds,
  onToggleControlId,
  onSetAllControls,
  onEditControl,
  variant = 'standard',
}) {
  const navigate = useNavigate();
  const isManual = variant === 'manual';
  // Column visibility for the manual (Selected ITGC Control) table. Lifted here
  // so the gear can live in the header beside "Add control".
  const [manualColumnVisibility, setManualColumnVisibility] = useState(
    MANUAL_ITGC_DEFAULT_VISIBILITY,
  );
  // Group the standard catalog by domain code for a tidy section per domain.
  const groupedCatalog = useMemo(() => {
    const map = new Map();
    for (const entry of standardCatalog) {
      const code = entry?.domainCode || '?';
      if (!map.has(code)) map.set(code, { domainName: entry?.domainName || '', entries: [] });
      const group = map.get(code);
      if (!group.domainName && entry?.domainName) group.domainName = entry.domainName;
      group.entries.push(entry);
    }
    return Array.from(map.entries()); // [[code, { domainName, entries }], ...]
  }, [standardCatalog]);

  return (
    <div className="space-y-4">
      {/* Standard / manual ITGC catalog picker */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 md:p-5 space-y-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <h2 className="text-sm font-semibold text-gray-900 mb-1">
              {isManual ? 'Selected ITGC Control' : 'Standard controls'}
            </h2>
            <p className="text-xs text-gray-500 max-w-2xl">
              {isManual
                ? 'Link org-created ITGC controls to this system. Risks come from existing system links set on the control page.'
                : "Pick the standard ITGC controls that apply to this system. Saving creates the org's editable copy of each selected control (and its risk) the first time it's picked — those rows are then editable on the ITGC Risks / ITGC Controls tabs (coming soon)."}
            </p>
          </div>
          {isManual ? (
            <div className="flex shrink-0 items-center gap-2">
              <ManualITGCColumnGear
                columnVisibility={manualColumnVisibility}
                setColumnVisibility={setManualColumnVisibility}
              />
              <button
                type="button"
                onClick={() => navigate('/sox-controls/new')}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors"
              >
                <Plus className="h-4 w-4" />
                <span>Add control</span>
              </button>
            </div>
          ) : null}
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 flex items-center gap-2">
            <AlertCircle className="h-4 w-4 text-red-600 shrink-0" />
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {catalogLoading ? (
          <p className="text-sm text-gray-500 inline-flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading ITGC controls…
          </p>
        ) : catalogError ? (
          <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 flex items-center gap-2">
            <AlertCircle className="h-4 w-4 text-red-600 shrink-0" />
            <p className="text-sm text-red-800">{catalogError}</p>
          </div>
        ) : groupedCatalog.length === 0 ? (
          <p className="text-sm text-gray-500">
            {isManual
              ? 'No Selected ITGC Control available for this organization.'
              : 'No ITGC controls available for this system.'}
          </p>
        ) : isManual ? (
          <ManualITGCTable
            standardCatalog={standardCatalog}
            selectedControlIds={selectedControlIds}
            onToggleControlId={onToggleControlId}
            onSetAllControls={onSetAllControls}
            onEditControl={onEditControl}
            columnVisibility={manualColumnVisibility}
            saving={saving}
          />
        ) : (
          <div className="space-y-5">
            {groupedCatalog.map(([domainCode, group]) => (
              <section key={domainCode}>
                <h3 className="text-xs font-bold uppercase tracking-wide text-purple-700 mb-2">
                  {group.domainName || domainCode}
                  {group.domainName && domainCode ? (
                    <span className="ml-2 text-purple-400 font-mono normal-case">{domainCode}</span>
                  ) : null}
                  <span className="ml-2 text-gray-400 font-medium normal-case">
                    {group.entries.length} control{group.entries.length === 1 ? '' : 's'}
                  </span>
                </h3>
                <ul className="space-y-2">
                  {group.entries.map((entry) => {
                    const code = entry.controlCode;
                    const controlId = entry.id;
                    const checked = selectedControlIds.has(controlId);
                    return (
                      <li key={controlId || code}>
                        <label
                          className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer ${
                            checked
                              ? 'border-blue-300 bg-blue-50/40'
                              : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          <input
                            type="checkbox"
                            className="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 disabled:cursor-not-allowed"
                            checked={checked}
                            onChange={() => onToggleControlId(controlId)}
                            disabled={saving}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap items-baseline gap-2">
                              <span className="text-xs font-mono text-gray-500">{code}</span>
                              <span className="text-sm font-medium text-gray-900">{entry.controlObjective}</span>
                              {entry.frequency ? (
                                <span className="text-[11px] text-gray-500 px-1.5 py-0.5 bg-gray-100 rounded">
                                  {entry.frequency}
                                </span>
                              ) : null}
                            </div>
                            {entry.isLinked ? (
                              <div className="mt-1">
                                <span className="inline-flex text-[11px] text-emerald-700 px-1.5 py-0.5 bg-emerald-50 border border-emerald-200 rounded">
                                  Selected · managed in Selected ITGC Control
                                </span>
                              </div>
                            ) : null}
                            <p className="text-xs text-gray-600 mt-1">{entry.controlWording}</p>
                            {(Array.isArray(entry.riskStatements) && entry.riskStatements.length > 0
                              ? entry.riskStatements
                              : entry.riskStatement
                                ? [entry.riskStatement]
                                : []
                            ).map((statement, riskIdx) => (
                              <p key={`${controlId}-risk-${riskIdx}`} className="text-[11px] text-red-700 mt-1">
                                <span className="font-semibold">Risk:</span> {statement}
                              </p>
                            ))}
                          </div>
                        </label>
                      </li>
                    );
                  })}
                </ul>
              </section>
            ))}
          </div>
        )}
      </div>

      {/* Read-only summary of what's actually saved on the server */}
      {currentControls.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 md:p-5">
          <h2 className="text-sm font-semibold text-gray-900 mb-2">Currently linked controls</h2>
          <ul className="flex flex-wrap gap-1.5">
            {currentControls.map((c) => (
              <li
                key={c.id}
                title={c.description || c.name}
                className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium border rounded ${
                  c.standardControlCode
                    ? 'bg-blue-50 text-blue-700 border-blue-200'
                    : 'bg-emerald-50 text-emerald-700 border-emerald-200'
                }`}
              >
                <span className="font-mono text-[10px] opacity-70">
                  {c.standardControlCode || c.code || '—'}
                </span>
                <span className="truncate max-w-[16rem]">{c.name}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex flex-wrap items-center justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          disabled={saving || !dirty}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Reset
        </button>
        <button
          type="button"
          onClick={onSave}
          disabled={!dirty || saving}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-blue-600 rounded-lg hover:bg-blue-700 disabled:bg-blue-300 disabled:cursor-not-allowed"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
}

// One SOC family (SOC 1 or SOC 2) — chip + collapsible Type I / Type II
// sub-tree. In edit mode the layout is unchanged (checkboxes + text
// inputs side by side); in read mode we only render the types that are
// actually enabled and stack them as an inline label-value list, so a
// "Type II only" coverage doesn't leave a half-empty grid with the
// remaining type centered in white space (the bug the screenshot
// flagged). Family chip is hidden in read mode when the family itself
// is off so we don't render an empty row.
// SOC reliance edit-mode layout. Renders four flat rows instead of the
// previous per-family 2-col sub-grid (which left awkward empty cells
// when the user only picked one Type, per teammate feedback):
//
//   [ ] SOC 1        [ ] SOC 2
//   [ ] Type I       [ ] Type II
//   ─ Type I periods ────────────
//   ─ Type II periods ───────────
//
// Trade-off worth flagging: the underlying schema stores Type I / Type II
// per family (soc1Type1, soc2Type1, …). This flat editor collapses that
// dimension — Type I checked applies to *both* SOC families that the
// user has selected, and the periods input is shared. The save layer
// (handleSave) already gates each per-family field on its parent
// family flag, so when only SOC 2 is checked we just write
// soc2Type1Periods and leave soc1Type1Periods empty. A pre-existing row
// with divergent SOC 1 vs SOC 2 type configs gets normalized on first
// save under this UI — acceptable for the rare case, per the teammate's
// "doesn't look good right now" feedback.
function SocFlatEditor({ coverageForm, setCoverageForm, inputClass }) {
  // Derived Type flags — true when at least one currently-checked SOC
  // family has that type set. Lets the read of Type I / Type II reflect
  // the union of the per-family schema.
  const typeI = (coverageForm.soc1 && coverageForm.soc1Type1) || (coverageForm.soc2 && coverageForm.soc2Type1);
  const typeII = (coverageForm.soc1 && coverageForm.soc1Type2) || (coverageForm.soc2 && coverageForm.soc2Type2);
  // Shared periods — prefer whichever family has a non-empty value so
  // existing data isn't visually lost on first load. On edit we mirror
  // the value back to both families' columns below.
  const typeIPeriods = coverageForm.soc1Type1Periods?.trim() ? coverageForm.soc1Type1Periods : coverageForm.soc2Type1Periods || '';
  const typeIIPeriods = coverageForm.soc1Type2Periods?.trim() ? coverageForm.soc1Type2Periods : coverageForm.soc2Type2Periods || '';

  // When the user toggles Type I/II, mirror to both per-family flags so
  // whichever SOC family is currently checked picks up the change. The
  // save gate (soc1 && soc1Type1, etc.) then naturally drops the value
  // for whichever family is unchecked.
  const setTypeI = (v) => setCoverageForm({ ...coverageForm, soc1Type1: v, soc2Type1: v });
  const setTypeII = (v) => setCoverageForm({ ...coverageForm, soc1Type2: v, soc2Type2: v });
  const setTypeIPeriods = (v) => setCoverageForm({ ...coverageForm, soc1Type1Periods: v, soc2Type1Periods: v });
  const setTypeIIPeriods = (v) => setCoverageForm({ ...coverageForm, soc1Type2Periods: v, soc2Type2Periods: v });

  const anyFamilyChecked = coverageForm.soc1 || coverageForm.soc2;

  return (
    <div className="space-y-3">
      {/* Row 1 — SOC family checkboxes side by side */}
      <div className="flex flex-wrap items-center gap-x-8 gap-y-2">
        <label className="flex items-center gap-2 text-sm font-medium text-gray-800 cursor-pointer">
          <input
            type="checkbox"
            checked={coverageForm.soc1}
            onChange={(e) => setCoverageForm({ ...coverageForm, soc1: e.target.checked })}
            className="h-4 w-4 text-amber-600 border-gray-300 rounded focus:ring-amber-500"
          />
          SOC&nbsp;1
        </label>
        <label className="flex items-center gap-2 text-sm font-medium text-gray-800 cursor-pointer">
          <input
            type="checkbox"
            checked={coverageForm.soc2}
            onChange={(e) => setCoverageForm({ ...coverageForm, soc2: e.target.checked })}
            className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
          />
          SOC&nbsp;2
        </label>
      </div>

      {/* Row 2 — Type checkboxes side by side, only when a family is checked */}
      {anyFamilyChecked && (
        <div className="flex flex-wrap items-center gap-x-8 gap-y-2 pl-1">
          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input
              type="checkbox"
              checked={typeI}
              onChange={(e) => setTypeI(e.target.checked)}
              className="h-4 w-4 text-slate-700 border-gray-300 rounded focus:ring-slate-500"
            />
            Type&nbsp;I
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input
              type="checkbox"
              checked={typeII}
              onChange={(e) => setTypeII(e.target.checked)}
              className="h-4 w-4 text-slate-700 border-gray-300 rounded focus:ring-slate-500"
            />
            Type&nbsp;II
          </label>
        </div>
      )}

      {/* Periods inputs — only for types that are currently checked. */}
      {anyFamilyChecked && (typeI || typeII) && (
        <div className="space-y-2 pl-1">
          {typeI && (
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
                Type&nbsp;I periods
              </label>
              <input
                type="text"
                value={typeIPeriods}
                onChange={(e) => setTypeIPeriods(e.target.value)}
                placeholder="Periods, comma-separated (e.g. Q1 2025, Q2 2025)"
                className={inputClass}
              />
            </div>
          )}
          {typeII && (
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
                Type&nbsp;II periods
              </label>
              <input
                type="text"
                value={typeIIPeriods}
                onChange={(e) => setTypeIIPeriods(e.target.value)}
                placeholder="Periods, comma-separated"
                className={inputClass}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function SocFamilyBlock({
  isEditing,
  chipClass,
  accentRingClass,
  accentTextClass,
  label,
  enabled,
  onToggle,
  type1Enabled,
  onToggleType1,
  type1Periods,
  onChangeType1Periods,
  type2Enabled,
  onToggleType2,
  type2Periods,
  onChangeType2Periods,
  inputClass,
}) {
  if (!isEditing && !enabled) return null;

  const noTypes = !type1Enabled && !type2Enabled;

  const renderTypeReadRow = (typeLabel, periods) => (
    <div className="flex items-baseline gap-2">
      <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 whitespace-nowrap">
        {typeLabel}
      </span>
      <span className="text-sm text-gray-800">{periods?.trim() ? periods : '—'}</span>
    </div>
  );

  return (
    <div>
      {isEditing ? (
        <label className="flex items-center gap-2 text-sm font-medium text-gray-800 cursor-pointer">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => onToggle(e.target.checked)}
            className={`h-4 w-4 ${accentTextClass} border-gray-300 rounded ${accentRingClass}`}
          />
          {label}
        </label>
      ) : (
        <span className={chipClass}>{label}</span>
      )}

      {enabled && (
        isEditing ? (
          <div className="mt-2 ml-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <SocTypeEditCell
              label="Type I"
              accentTextClass={accentTextClass}
              accentRingClass={accentRingClass}
              enabled={type1Enabled}
              onToggle={onToggleType1}
              periods={type1Periods}
              onChangePeriods={onChangeType1Periods}
              inputClass={inputClass}
              placeholder="Periods, comma-separated (e.g. Q1 2025, Q2 2025)"
            />
            <SocTypeEditCell
              label="Type II"
              accentTextClass={accentTextClass}
              accentRingClass={accentRingClass}
              enabled={type2Enabled}
              onToggle={onToggleType2}
              periods={type2Periods}
              onChangePeriods={onChangeType2Periods}
              inputClass={inputClass}
              placeholder="Periods, comma-separated"
            />
          </div>
        ) : noTypes ? null : (
          <div className="mt-2 ml-6 flex flex-col gap-1">
            {type1Enabled && renderTypeReadRow('Type I', type1Periods)}
            {type2Enabled && renderTypeReadRow('Type II', type2Periods)}
          </div>
        )
      )}
    </div>
  );
}

// One Type I / Type II edit cell. Checkbox + (optional) periods input
// when the type is checked. Extracted so the family block stays scannable.
function SocTypeEditCell({
  label,
  accentTextClass,
  accentRingClass,
  enabled,
  onToggle,
  periods,
  onChangePeriods,
  inputClass,
  placeholder,
}) {
  return (
    <div>
      <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer mb-1">
        <input
          type="checkbox"
          checked={enabled}
          onChange={(e) => onToggle(e.target.checked)}
          className={`h-4 w-4 ${accentTextClass} border-gray-300 rounded ${accentRingClass}`}
        />
        {label}
      </label>
      {enabled && (
        <input
          type="text"
          value={periods}
          onChange={(e) => onChangePeriods(e.target.value)}
          placeholder={placeholder}
          className={inputClass}
        />
      )}
    </div>
  );
}

export default SystemDetail;
